import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import { IdParams } from 'types/api'
import { IPaginationQueryInfo } from 'types/pagination'
import {
  ISubmissionEvaluation,
  ISubmissionEvaluationExtended,
  ISubmissionEvaluations,
  SubmissionEvaluationParams,
} from 'types/submissions/submissions-evaluation'

type ISubmissionEvaluationsPaginationQueryInfo = IPaginationQueryInfo &
  SubmissionEvaluationParams

export const submissionEvaluationApi = createApi({
  reducerPath: 'submissionEvaluation',
  baseQuery,
  tagTypes: ['SubmissionEvaluations', 'SubmissionEvaluation'],
  endpoints: (builder) => ({
    submissionEvaluations: builder.query<
      ISubmissionEvaluations,
      ISubmissionEvaluationsPaginationQueryInfo
    >({
      query: ({ page, size, submissionId, tenderId, statuses }) => {
        const params = new URLSearchParams()

        if (size) {
          params.set('size', String(size))
        }
        params.set('page', String(page))

        if (submissionId && submissionId.length > 0) {
          params.set('submissionId', submissionId)
        }
        if (tenderId && tenderId.length > 0) {
          params.set('tenderId', tenderId)
        }

        if (statuses && statuses.length > 0) {
          statuses.forEach((status) => params.append('statuses', status.toString()))
        }

        return {
          url: '/submission-evaluation',
          method: 'get',
          params,
        }
      },
      providesTags: ['SubmissionEvaluations'],
    }),

    submissionEvaluation: builder.query<ISubmissionEvaluation, IdParams>({
      query: ({ id }) => `/submission-evaluation/${id}`,
      providesTags: ['SubmissionEvaluation'],
    }),

    saveSubmissionEvaluation: builder.mutation<
      ISubmissionEvaluation,
      Partial<ISubmissionEvaluation>
    >({
      query: (body) => ({
        url: `/submission-evaluation`,
        body,
        method: 'put',
      }),
    }),

    confirmSubmissionEvaluation: builder.mutation<
      ISubmissionEvaluation,
      Partial<ISubmissionEvaluation>
    >({
      query: (body) => ({
        url: `/submission-evaluation/${body.submissionEvaluationId}/confirm`,
        body,
        method: 'put',
      }),
    }),
    rejectSubmissionEvaluation: builder.mutation<
      null,
      Partial<ISubmissionEvaluation>
    >({
      query: (body) => ({
        url: `/submission-evaluation/${body.submissionEvaluationId}/reject`,
        body,
        method: 'put',
      }),
    }),
    changeRequestSubmissionEvaluation: builder.mutation<
      ISubmissionEvaluation,
      Partial<ISubmissionEvaluation>
    >({
      query: (body) => ({
        url: `/submission-evaluation/${body.submissionId}/change-request`,
        body,
        method: 'put',
      }),
    }),
    setWinnerSubmissionEvaluation: builder.mutation<
      ISubmissionEvaluationExtended,
      Partial<ISubmissionEvaluationExtended>
    >({
      query: (body) => ({
        url: `/submission-evaluation/${body.id}/set-winner`,
        body,
        method: 'put',
      }),
    }),
  }),
})

export const {
  useChangeRequestSubmissionEvaluationMutation,
  useSaveSubmissionEvaluationMutation,
  useConfirmSubmissionEvaluationMutation,
  useRejectSubmissionEvaluationMutation,
  useSubmissionEvaluationQuery,
  useSubmissionEvaluationsQuery,
  useSetWinnerSubmissionEvaluationMutation,
} = submissionEvaluationApi
